import { useState } from "react";
import {
  DataTable,
  DataTableColumnHeader,
  Button,
  Tag,
  Checkbox,
  InputField,
  Textarea,
  RadioGroup,
  RadioGroupItem,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@jem-hr/jem-ui";
import { ColumnDef } from "@tanstack/react-table";
import { Plus, Paperclip, Pencil, Trash2, ChevronDown, SquareActivity, Download } from "lucide-react";
import { BranchFilter } from "../components/BranchFilter";

interface Announcement {
  id: string;
  title: string;
  category: "General" | "Vacancy";
  views: string;
  launchDate: string;
  closeDate: string;
  hasAttachment: boolean;
  status: "EXPIRED" | "DRAFT" | "LIVE" | "SCHEDULED";
  tags?: string[];
  source?: "local" | "multimessage";
  sentBy?: string;
  parentCompany?: string;
}

const data: Announcement[] = [
  {
    id: "m1",
    title: "Q1 Operational Update",
    category: "General",
    views: "289/342",
    launchDate: "08/03/2026 09:00",
    closeDate: "15/03/2026 09:00",
    hasAttachment: true,
    status: "LIVE",
    source: "multimessage",
    sentBy: "Sarah Johnson",
    parentCompany: "KFC Franchise Group",
  },
  {
    id: "m2",
    title: "New Uniform Policy – Effective April",
    category: "General",
    views: "310/342",
    launchDate: "05/03/2026 14:30",
    closeDate: "12/03/2026 14:30",
    hasAttachment: true,
    status: "LIVE",
    source: "multimessage",
    sentBy: "Sarah Johnson",
    parentCompany: "KFC Franchise Group",
  },
  {
    id: "m3",
    title: "Easter Trading Hours",
    category: "General",
    views: "0/342",
    launchDate: "01/03/2026 08:00",
    closeDate: "05/04/2026 08:00",
    hasAttachment: false,
    status: "SCHEDULED",
    source: "multimessage",
    sentBy: "Mark Peters",
    parentCompany: "KFC Franchise Group",
  },
  {
    id: "1",
    title: "Friday Motivation",
    category: "General",
    views: "1/1",
    launchDate: "04/02/2026 11:56",
    closeDate: "07/02/2026 11:56",
    hasAttachment: false,
    status: "EXPIRED",
    tags: ["General", "Company News"],
  },
  {
    id: "2",
    title: "Receptionist",
    category: "Vacancy",
    views: "1/1",
    launchDate: "30/01/2026 11:33",
    closeDate: "31/01/2026 06:00",
    hasAttachment: false,
    status: "EXPIRED",
    tags: ["Vacancy"],
  },
  {
    id: "3",
    title: "Amber Test 2",
    category: "Vacancy",
    views: "1/1",
    launchDate: "29/01/2026 14:13",
    closeDate: "05/02/2026 14:13",
    hasAttachment: false,
    status: "EXPIRED",
    tags: ["Vacancy"],
  },
  {
    id: "4",
    title: "Test Thando 3",
    category: "General",
    views: "1/1",
    launchDate: "29/01/2026 14:00",
    closeDate: "05/02/2026 14:00",
    hasAttachment: false,
    status: "EXPIRED",
    tags: ["General"],
  },
  {
    id: "5",
    title: "Verifying New Approach (Rich...",
    category: "General",
    views: "1/1",
    launchDate: "29/01/2026 13:38",
    closeDate: "05/02/2026 13:38",
    hasAttachment: false,
    status: "EXPIRED",
    tags: ["General"],
  },
  {
    id: "6",
    title: "Verifying Old Approach (Rich...",
    category: "General",
    views: "1/1",
    launchDate: "29/01/2026 13:32",
    closeDate: "05/02/2026 13:32",
    hasAttachment: false,
    status: "EXPIRED",
    tags: ["General"],
  },
  {
    id: "7",
    title: "Amber Test",
    category: "General",
    views: "1/1",
    launchDate: "29/01/2026 11:26",
    closeDate: "30/01/2026 11:26",
    hasAttachment: false,
    status: "EXPIRED",
    tags: ["General"],
  },
  {
    id: "8",
    title: "Test",
    category: "General",
    views: "0/0",
    launchDate: "29/01/2026 09:32",
    closeDate: "05/02/2026 09:32",
    hasAttachment: false,
    status: "DRAFT",
    tags: ["General"],
  },
  {
    id: "9",
    title: "Test",
    category: "General",
    views: "1/1",
    launchDate: "28/01/2026 15:05",
    closeDate: "04/02/2026 15:05",
    hasAttachment: false,
    status: "EXPIRED",
    tags: ["General"],
  },
];

const columns: ColumnDef<Announcement>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) =>
      row.original.source === "multimessage" ? null : (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "title",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Content" />
    ),
    cell: ({ row }) => (
      <div>
        <div className="flex items-center gap-xxs">
          <p className="text-sm font-semibold text-greyscale-text-title">
            {row.original.title}
          </p>
        </div>
        <div className="flex items-center gap-xxs">
          <Tag variant="pink">{row.original.category}</Tag>
          {row.original.source === "multimessage" && (
            <span className="text-xs text-secondary-pink-900">
              · via KFC Holding Group (YUM)
            </span>
          )}
        </div>
      </div>
    ),
  },
  {
    accessorKey: "views",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Views" />
    ),
  },
  {
    accessorKey: "launchDate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Launch Date" />
    ),
  },
  {
    accessorKey: "closeDate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Close Date" />
    ),
  },
  {
    accessorKey: "tags",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Tags/Segments" />
    ),
    cell: ({ row }) => {
      const tags = row.original.tags;
      if (!tags || tags.length === 0 || row.original.source === "multimessage") {
        return <span className="text-sm text-greyscale-text-caption">-</span>;
      }
      return (
        <div className="flex items-center gap-xxs">
          <span className="text-sm text-greyscale-text-body">View all</span>
          <ChevronDown size={14} className="text-greyscale-text-caption" />
        </div>
      );
    },
  },
  {
    accessorKey: "hasAttachment",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Attachments" />
    ),
    cell: ({ row }) =>
      row.original.hasAttachment ? (
        <Paperclip size={16} className="text-greyscale-text-caption" />
      ) : (
        <span className="text-sm text-greyscale-text-caption">-</span>
      ),
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const statusMap: Record<string, string> = {
        EXPIRED: "failed",
        DRAFT: "drafted",
        LIVE: "success",
        SCHEDULED: "processing",
      };
      return (
        <Tag variant={statusMap[row.original.status] as any}>
          {row.original.status}
        </Tag>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row }) => (
      <TooltipProvider delayDuration={300}>
        <div className="flex items-center gap-xxs">
          {row.original.source !== "multimessage" && row.original.status === "EXPIRED" && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-greyscale-text-caption hover:text-greyscale-text-title">
                    <SquareActivity size={16} />
                  </button>
                </TooltipTrigger>
                <TooltipContent variant="dark">Analytics</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-greyscale-text-caption hover:text-greyscale-text-title">
                    <Download size={16} />
                  </button>
                </TooltipTrigger>
                <TooltipContent variant="dark">Download</TooltipContent>
              </Tooltip>
            </>
          )}
          {row.original.source !== "multimessage" && row.original.status === "DRAFT" && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-greyscale-text-caption hover:text-greyscale-text-title">
                    <Pencil size={16} />
                  </button>
                </TooltipTrigger>
                <TooltipContent variant="dark">Edit</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-greyscale-text-caption hover:text-red-500">
                    <Trash2 size={16} />
                  </button>
                </TooltipTrigger>
                <TooltipContent variant="dark">Delete</TooltipContent>
              </Tooltip>
            </>
          )}
        </div>
      </TooltipProvider>
    ),
  },
];

export function Announcements() {
  const [showForm, setShowForm] = useState(false);

  return (
    <div>
      {/* Header */}
      <div
        className="flex items-center justify-between -mx-xl -mt-lg px-xl py-lg mb-md"
        style={{ backgroundColor: "rgba(237,244,251,0.5)" }}
      >
        <div>
          <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
            Announcements
          </h1>
          <p className="py-[6px] text-sm font-regular leading-18 text-greyscale-text-body">
            Send important updates and messages to your employees, all in one
            place.
          </p>
        </div>
        {!showForm && (
          <Button leftIcon={<Plus size={16} />} onClick={() => setShowForm(true)}>
            Create New
          </Button>
        )}
      </div>

      {showForm ? (
        <div>
          <div className="max-w-[850px]">
            {/* Form heading */}
            <p className="text-base font-semibold text-greyscale-text-title mb-md">
              Create a new announcement
            </p>

            <div className="flex flex-col gap-md">
            {/* Select a template */}
            <div className="flex flex-col gap-xxxs">
              <div>
                <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                  Select a templates
                </p>
                <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                  This is a description if needed
                </p>
              </div>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select Title" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="template-1">Template 1</SelectItem>
                  <SelectItem value="template-2">Template 2</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <InputField
              label="Title"
              description="This is a description if needed"
              placeholder="Placeholder text"
            />

            {/* Message */}
            <div className="flex flex-col gap-xxxs">
              <div>
                <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                  Message
                </p>
                <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                  This is a description if needed
                </p>
              </div>
              <Textarea
                placeholder="Placeholder text"
                className="min-h-[115px]"
              />
            </div>

            {/* Attach Media */}
            <Button leftIcon={<Paperclip size={11} />} className="w-fit">
              Attach Media
            </Button>

            {/* Date range */}
            <div className="flex flex-col gap-xxxs">
              <div>
                <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                  Date range
                </p>
                <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                  This is a description if needed
                </p>
              </div>
              <RadioGroup defaultValue="default-1" className="flex gap-xxxs">
                <div className="flex items-center gap-xxs">
                  <RadioGroupItem value="default-1" id="default-1" />
                  <Label htmlFor="default-1" className="text-sm">Default</Label>
                </div>
                <div className="flex items-center gap-xxs">
                  <RadioGroupItem value="default-2" id="default-2" />
                  <Label htmlFor="default-2" className="text-sm">Default</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Select tags */}
            <div className="flex flex-col gap-xxxs">
              <div>
                <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                  Select tags
                </p>
                <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                  This is a description if needed
                </p>
              </div>
              <BranchFilter
                branches={["General", "Vacancy", "Urgent", "Company News"]}
                selectedBranches={[]}
                label=""
                placeholder="Select Title"
                searchPlaceholder="Search tags..."
                className="max-w-full"
              />
            </div>

          </div>
          </div>

          {/* Footer buttons */}
          <div className="border-t border-greyscale-border mt-lg" />
          <div className="flex items-center justify-between py-lg">
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Go Back
            </Button>
            <div className="flex items-center gap-xxs">
              <Button variant="outline">
                Save draft
              </Button>
              <Button>
                Send to 74 employees
              </Button>
            </div>
          </div>
        </div>
      ) : (
        /* Table */
        <DataTable
          columns={columns}
          data={data}
          showPagination
          showToolbar={false}
        />
      )}
    </div>
  );
}
